import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const getStopRange = (startTime, endTime, timezone) => {
  const range = getAppointmentDate({
    scheduled_window_started_at_in_stop_timezone: startTime,
    scheduled_window_ended_at_in_stop_timezone: endTime,
    timezone,
  });

  return range.time;
};

export const getStopTime = (time, timezone, includesDate = false) => {
  // Date object converts to local timezone without specifying
  // And timezones aren't assigned via offset in Date object
  // -05:00 UTC if not specified will change to GMT+5 instead CST for example
  // We can manually pick the offset off the timestamp or use library like luxon
  const options = {
    // hour12: false,
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h23',
  };
  if (includesDate) {
    options.month = 'short';
    options.day = 'numeric';
    options.year = 'numeric';
  }
  if (!time) return '';
  if (!timezone) {
    const offset = time.split('-')[3];
    if (offset) {
      options.timeZone = `-${offset}`;
      // options.timeZoneName = 'short';
    }
  } else {
    options.timeZone = timezone;
    options.timeZoneName = 'short';
  }
  const formattedTime = new Intl.DateTimeFormat('en-US', options).format(
    new Date(time)
  );
  const hr = formattedTime
    .split(':')[0]
    .replace(/^(?:0+(?=[1-9])|0+(?=0$))/gm, '');
  const min = formattedTime.split(':')[1];
  return `${hr}:${min}`;
};

export const getDateArray = (time, timezone, conditions = {}) => {
  const { month = 'short', day = 'numeric', year = 'numeric' } = conditions;
  const options = {
    day: day,
    month: month,
    year: year,
  };
  if (!time) return [];
  if (!timezone) {
    const offset = time.split('-')[3];
    if (offset) {
      options.timeZone = `-${offset}`;
    }
  } else {
    options.timeZone = timezone;
  }
  return new Intl.DateTimeFormat('en-US', options).formatToParts(
    new Date(time)
  );
};

export const getStopDay = (time, timezone, conditions = {}) => {
  const {
    includesDay = true,
    includesMonth = true,
    includesYear = true,
    yearDigits = 4,
    monthNumeric = false,
  } = conditions;
  const options = {};
  if (includesYear) {
    options.year = yearDigits === 2 ? '2-digit' : 'numeric';
  }
  if (includesMonth) {
    options.month = monthNumeric ? 'numeric' : 'short';
  }
  if (includesDay) {
    options.day = 'numeric';
  }
  if (!time) return '';
  if (!timezone) {
    const offset = time.split('-')[3];
    if (offset) {
      options.timeZone = `-${offset}`;
    }
  } else {
    options.timeZone = timezone;
  }
  return new Intl.DateTimeFormat('en-US', options).format(new Date(time));
  // .replace(',', '');
};

export const getLocalTime = (time) => {
  if (!time) return '';
  return `${new Intl.DateTimeFormat('en-US', {
    hour12: false,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZoneName: 'short',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date(time))}`;
  // .replace(',', '')}`;
};

export const getTimeFromNow = (endISO) => {
  const startDate = new Date(endISO);
  const endDate = new Date();
  const seconds = Math.abs(startDate.getTime() - endDate.getTime()) / 1000;
  switch (true) {
    case seconds < 60:
      return `${seconds} seconds ago`;
    case seconds < 3600:
      return `${Math.round(seconds / 60)} minutes ago`;
    case seconds < 86400:
      return `${Math.round(seconds / 3600)} hours ago`;
    case seconds < 604800:
      return `${Math.round(seconds / 86400)} days ago`;
    default:
      return `${Math.round(seconds / 604800)} weeks ago`;
  }
};

export const getHoursFromNow = (endISO) => {
  const startDate = new Date(endISO);
  const endDate = new Date();
  const milliseconds = (startDate.getTime() - endDate.getTime()) / 1000;
  const hours = milliseconds / (60 * 60);
  return Math.round(hours);
};

export const getDaysFromNow = (endISO) => {
  const startDate = new Date(endISO);
  const endDate = new Date();
  const milliseconds = (startDate.getTime() - endDate.getTime()) / 1000;
  const days = milliseconds / (60 * 60 * 24);
  return Math.round(days);
};

export const getAppointmentDate = ({
  scheduled_window_started_at_in_stop_timezone,
  scheduled_window_ended_at_in_stop_timezone,
  timezone,
  scheduled_window_started_at_date,
}) => {
  const stopWindowStartDateArray = getDateArray(
    scheduled_window_started_at_in_stop_timezone ||
      scheduled_window_started_at_date,
    timezone
  );
  const stopWindowStartDay = stopWindowStartDateArray.find(
    ({ type }) => type === 'day'
  )?.value;

  const stopWindowStartMonth = stopWindowStartDateArray.find(
    ({ type }) => type === 'month'
  )?.value;

  const stopWindowStartYear = stopWindowStartDateArray.find(
    ({ type }) => type === 'year'
  )?.value;

  const stopWindowEndDateArray = getDateArray(
    scheduled_window_ended_at_in_stop_timezone,
    timezone
  );
  const stopWindowEndDay = stopWindowEndDateArray.find(
    ({ type }) => type === 'day'
  )?.value;

  const stopWindowEndMonth = stopWindowEndDateArray.find(
    ({ type }) => type === 'month'
  )?.value;

  const stopWindowEndYear = stopWindowEndDateArray.find(
    ({ type }) => type === 'year'
  )?.value;

  const stopWindowStartTime = getStopTime(
    scheduled_window_started_at_in_stop_timezone,
    timezone
  );
  const stopWindowEndTime = getStopTime(
    scheduled_window_ended_at_in_stop_timezone,
    timezone
  );

  const isSameYear =
    !!stopWindowStartYear && stopWindowStartYear === stopWindowEndYear;

  const isSameMonth =
    isSameYear &&
    !!stopWindowStartMonth &&
    stopWindowStartMonth === stopWindowEndMonth;

  const isSameDay =
    isSameMonth &&
    !!stopWindowStartDay &&
    stopWindowStartDay === stopWindowEndDay;

  const isSameTime =
    isSameDay &&
    !!stopWindowStartTime &&
    stopWindowStartTime === stopWindowEndTime;

  let stopWindowDay = stopWindowStartDay || stopWindowEndDay;
  let stopWindowTime = stopWindowStartTime || stopWindowEndTime;

  if (!stopWindowDay && !stopWindowTime) {
    return {
      day: '',
      time: '',
    };
  }

  if (isSameDay) {
    stopWindowDay = `${stopWindowStartMonth} ${stopWindowStartDay}, ${stopWindowStartYear}`;
  } else if (isSameMonth) {
    stopWindowDay = `${stopWindowStartMonth} ${stopWindowStartDay} - ${stopWindowEndDay}, ${stopWindowStartYear}`;
  } else if (isSameYear) {
    stopWindowDay = `${stopWindowStartMonth} ${stopWindowStartDay} - ${stopWindowEndMonth} ${stopWindowEndDay}, ${stopWindowStartYear}`;
  } else {
    const stopWindowStart =
      (stopWindowStartMonth ? `${stopWindowStartMonth} ` : '') +
      (stopWindowStartDay || '') +
      (stopWindowStartYear ? `, ${stopWindowStartYear}` : '');
    const stopWindowEnd =
      (stopWindowEndMonth ? `${stopWindowEndMonth} ` : '') +
      (stopWindowEndDay || '') +
      (stopWindowEndYear ? `, ${stopWindowEndYear}` : '');
    stopWindowDay =
      stopWindowStart && stopWindowEnd
        ? `${stopWindowStart} - ${stopWindowEnd}`
        : `${stopWindowStart}${stopWindowEnd}`;
  }
  if (!isSameTime) {
    stopWindowTime =
      getStopTime(
        scheduled_window_started_at_in_stop_timezone,
        stopWindowEndTime ? '' : timezone
      ) + (stopWindowEndTime ? ` - ${stopWindowEndTime}` : '');
  }
  return {
    day: stopWindowDay,
    time: stopWindowTime,
  };
};
